import { useMutation } from '@tanstack/react-query'
import React from 'react'

import ActionButton, { ActionButtonProps } from '@/components/misc/ActionButton'
import { Button } from '@/components/ui/button'
import useAppDispatch from '@/hooks/useAppDispatch'
import { useIsRoute } from '@/hooks/useIsRoute'
import useNotyf from '@/hooks/useNotyf'
import { COMMERCIAL_AGREEMENT_REFERRAL_DIRECTION } from '@/lib/constants'
import { getIdFromIri } from '@/lib/helpers/helperFunctions'
import { ROUTES } from '@/lib/routes'
import referralMetadataService from '@/lib/services/referralMetadataService'
import { cn } from '@/lib/utils'
import { reset } from '@/redux/slices/webReferral'

type Props = React.ComponentProps<typeof Button> & {
  toCompanyIri: IRI
  children: React.ReactNode
  onCanRefer: () => void
  showSpinner?: ActionButtonProps['showSpinner']
  isProcessing?: boolean
}

const ReferClientButton = React.forwardRef<HTMLButtonElement, Props>(
  ({ style, toCompanyIri, onCanRefer, ...props }, ref) => {
    const notyf = useNotyf()
    const variant = props.variant || 'primary'
    const dispatch = useAppDispatch()
    const isRoute = useIsRoute()

    const canReferClientMutation = useMutation({
      mutationFn: () =>
        referralMetadataService.checkCanRefer({ toCompany: toCompanyIri }),
      onSuccess: (canReferData) => {
        // We can't refer to adviser
        if (!canReferData.canRefer) {
          notyf.open({
            type: canReferData.missingAgreementDirection ? 'info' : 'error',
            message: canReferData.cannotReferReason!,
          })

          // If missing agreement direction is set, we need to redirect to add
          // panel agreement page
          if (canReferData.missingAgreementDirection) {
            return ROUTES.addRelationshipAgreement({
              params: { panelId: getIdFromIri(canReferData.panel).toString() },
              searchParams: {
                referralDirection:
                  COMMERCIAL_AGREEMENT_REFERRAL_DIRECTION.OUTGOING,
              },
            })
          }

          return
        }

        // If we're navigating to the refer client page from another page, reset the flow
        if (!isRoute(ROUTES._referClient)) {
          dispatch(reset())
        }

        onCanRefer()
      },
      onError: () => {
        notyf.error(
          'There was a problem selecting this adviser. Please refresh the page and try again',
        )
      },
    })

    const isProcessing =
      !!props.isProcessing || canReferClientMutation.isPending
    const shouldBeDisabled = props.disabled || isProcessing

    return (
      <ActionButton
        {...props}
        disabled={shouldBeDisabled}
        isProcessing={isProcessing}
        isProcessingText={props.children}
        onClick={() => canReferClientMutation.mutate()}
        ref={ref}
        variant={variant}
        className={cn(props.className)}
        style={style}
      />
    )
  },
)

export default ReferClientButton
